import { Banner, Select, Switch, Tag, TextLink, Tooltip, Typography } from '@neo4j-ndl/react';
import { getFlag } from '@nx/launch-darkly-service';
import type { Instance, Project } from '@nx/state';
import { NEO4J_MANAGED_KEY, TIER, useEncryptionKeys } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { useNavigate } from 'react-router';

import { MINIMUM_GDS_PLUGIN_VECTOR_MEMORY } from '../constants';
import { encryptionKeysToOptions, gbStringToInt } from '../entities/helpers';
import type { InstanceFormData } from '../entities/model';
import { BaseInstanceCreateSection, BaseSectionItemsWrapper } from './base-section';

type Props = {
  data: InstanceFormData;
  onDataChange: (data: Partial<InstanceFormData>) => void;
  project: Project;
  instance?: Instance;
};

type InstanceVectorOptimizedFormData = Pick<InstanceFormData, 'size' | 'version' | 'vectorOptimized' | 'tier'>;

const vectorOptimizedDisabledReason = (data: InstanceVectorOptimizedFormData) => {
  if (isNullish(data.size)) {
    return 'You must select a size before enabling this feature';
  }

  if (![TIER.PROFESSIONAL, TIER.MTE, TIER.ENTERPRISE].includes(data.tier)) {
    return 'Vector index configuration is not available for this tier';
  }

  if (data.version !== '5') {
    return 'Vector index configuration is not available for the selected version';
  }

  if (gbStringToInt(data.size.memory) < MINIMUM_GDS_PLUGIN_VECTOR_MEMORY) {
    return '4GB memory or greater is required for the vector index configuration';
  }

  return null;
};

export const AdditionalSettingsSection = ({ data, onDataChange, project, instance }: Props) => {
  const cloningStoreFormatOptions = getFlag('cloningStoreFormatOptions', false);
  const provideStoreFormatOptions =
    isNotNullish(instance?.storeFormat) && instance.storeFormat !== 'block' && cloningStoreFormatOptions;

  const { keys } = useEncryptionKeys();
  const navigate = useNavigate();

  const filteredKeys = keys.filter((key) => key.tier === data.tier && key.region === data.region);

  const encryptionKeyOptions = encryptionKeysToOptions(filteredKeys, isNullish(instance?.encryptionKeyRef));

  const handleChange = (value?: string) => {
    if (isNotNullish(value)) {
      onDataChange({ encryptionKeyRef: value });
    }
  };
  const disabledReason = vectorOptimizedDisabledReason(data);
  const isDisabled = isNotNullish(disabledReason);
  const label = 'Toggle on Allocate vector index memory';

  const handleVectorOptimizedChange = (value: boolean) => {
    onDataChange({ vectorOptimized: value });
  };

  const handleMultiDatabaseChange = (multiDatabase: boolean) => {
    onDataChange({ ...data, multiDatabase });
  };

  const handleMigrateToBlockChange = (migrateToBlock: boolean) => {
    onDataChange({ ...data, migrateToBlock });
  };

  const showCmekWarning = project.capabilities.cmek && data.encryptionKeyRef !== NEO4J_MANAGED_KEY;

  return (
    <BaseInstanceCreateSection title="Additional settings">
      <BaseSectionItemsWrapper>
        <div className="flex w-full grow items-center justify-between">
          <div className="flex flex-col gap-1">
            <Typography variant="subheading-small">Vector-optimized configuration</Typography>
            <Typography variant="body-small" className="text-neutral-text-weak">
              Optimize Neo4j for GraphRAG, GenAI, and semantic search applications that index and search vector
              embeddings. See{' '}
              <TextLink href="https://neo4j.com/docs/aura/managing-instances/vector-optimization/" type="external">
                documentation
              </TextLink>{' '}
              for details.
            </Typography>
          </div>
          <Tooltip type="simple" isPortaled={false} isDisabled={!isDisabled}>
            <Tooltip.Trigger>
              <Switch
                htmlAttributes={{
                  'aria-label': label,
                  'data-testid': 'vector-optimized-switch',
                }}
                ariaLabel={label}
                isChecked={data.vectorOptimized}
                onChange={(e) => handleVectorOptimizedChange(e.target.checked)}
                isDisabled={isDisabled}
              />
            </Tooltip.Trigger>
            <Tooltip.Content>{disabledReason}</Tooltip.Content>
          </Tooltip>{' '}
        </div>
      </BaseSectionItemsWrapper>

      {[TIER.ENTERPRISE, TIER.AURA_DSE].includes(data.tier) && isNullish(instance) && (
        <>
          {project.capabilities.cmek && (
            <BaseSectionItemsWrapper>
              <div className="flex w-full grow items-center justify-between">
                <div className="flex w-full flex-col gap-1">
                  <Typography variant="subheading-small">Encryption key</Typography>
                  <div className="@sm:grid-cols-2 @sm:grid-rows-2 grid w-full auto-rows-fr gap-y-4">
                    <Typography variant="body-small" className="text-neutral-text-weak col-start-1">
                      Protect your data using either Neo4j-managed or customer-managed encryption. <br />
                      Set up your own customer-managed encryption key{' '}
                      <TextLink
                        as="button"
                        htmlAttributes={{
                          onClick: () => {
                            void navigate(`/projects/${project.id}/settings/cmek`);
                          },
                        }}
                      >
                        here
                      </TextLink>
                      .
                    </Typography>
                    <Select
                      size="medium"
                      type="select"
                      className="col-start-1"
                      selectProps={{
                        value: encryptionKeyOptions.find((key) => key.value === data.encryptionKeyRef),
                        options: encryptionKeyOptions,
                        onChange: (e) => handleChange(e?.value),
                      }}
                      // errorText={errorMessage}
                      aria-label="Encryption Settings"
                    />
                  </div>
                  {showCmekWarning && (
                    <Banner
                      description="By encrypting this instance with a Customer Managed Key, you take full responsibility for the management and security of the key. Neo4j cannot recover your data if the key is lost or invalid. There is a risk of permanent data loss."
                      type="warning"
                      hasIcon
                      usage="inline"
                    />
                  )}
                </div>
              </div>
            </BaseSectionItemsWrapper>
          )}
          {provideStoreFormatOptions && (
            <BaseSectionItemsWrapper>
              <div className="flex w-full grow items-center justify-between">
                <div className="flex flex-col gap-2">
                  <div className="flex flex-row items-center gap-2">
                    <Typography variant="subheading-small">
                      {' '}
                      Migrate database store format to block storage (recommended)
                    </Typography>
                    <Tag size="small">Early Access Preview</Tag>
                  </div>

                  <Typography variant="body-small" as="div" className="text-neutral-text-weak">
                    Migrate the database store format to block storage to improve performance and reduce costs. See{' '}
                    <TextLink
                      type="external"
                      href="https://neo4j.com/docs/operations-manual/current/database-internals/store-formats/"
                    >
                      documentation
                    </TextLink>{' '}
                    for details.
                  </Typography>
                </div>

                <Tooltip type="simple" isPortaled={false}>
                  <Tooltip.Trigger>
                    <Switch
                      htmlAttributes={{
                        'aria-label': label,
                        'data-testid': 'migrate-to-block-switch',
                      }}
                      ariaLabel={label}
                      isChecked={data.migrateToBlock ?? false}
                      onChange={(e) => handleMigrateToBlockChange(e.target.checked)}
                    />
                  </Tooltip.Trigger>
                </Tooltip>
              </div>
            </BaseSectionItemsWrapper>
          )}
          {project.capabilities.multi_db_enterprise && isNullish(instance) && (
            <BaseSectionItemsWrapper>
              <div className="flex w-full grow items-center justify-between">
                <div className="flex flex-col gap-2">
                  <div className="flex flex-row items-center gap-2">
                    <Typography variant="subheading-small">Multiple databases</Typography>
                    <Tag size="small">Early Access Preview</Tag>
                  </div>

                  <Typography variant="body-small" as="div" className="text-neutral-text-weak">
                    Use multiple databases in a single instance
                  </Typography>
                </div>

                <Tooltip type="simple" isPortaled={false}>
                  <Tooltip.Trigger>
                    <Switch
                      htmlAttributes={{
                        'aria-label': 'Toggle to enable multiple databases in instance',
                        'data-testid': 'multidb-switch',
                      }}
                      ariaLabel="Toggle to enable multiple databases in instance"
                      isChecked={data.multiDatabase ?? false}
                      onChange={(e) => handleMultiDatabaseChange(e.target.checked)}
                    />
                  </Tooltip.Trigger>
                </Tooltip>
              </div>
            </BaseSectionItemsWrapper>
          )}
        </>
      )}
    </BaseInstanceCreateSection>
  );
};
